import { h } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useDispatch, useSelector } from "react-redux";

import Pagination from "./Pagination.jsx";
import {
  selectCurrentFilters,
  setCurrentPage,
  setFavoritesChanged,
  setGlobalError,
  setLastSeen,
  setLoading,
} from "../store";
import { sendError } from "../errors.js";

const PAGE_SIZE = 10;

export default function ServerPaginated({
  id,
  renderEmptyState,
  fetchingMethod,
  storingMethod,
  render,
}) {
  const isLoading = useSelector((state) => state.ui.loading);
  const items = useSelector((state) => state.talentPool.items[id] || []);
  const pageMeta = useSelector(
    (state) => state.talentPool.itemsMeta[id] || { page: 1, total: 0 }
  );
  const currentPage = useSelector((state) => state.talentPool.page[id] || 1);
  const currentFilters = useSelector(selectCurrentFilters);
  const visibleFilters = useSelector((state) => state.talentPool.filters);
  const needsRefetching = useSelector(
    (state) => id === "favorites" && state.talentPool.favoritesChanged
  );

  const [lastCurrentFilters, setLastCurrentFilters] = useState(
    JSON.stringify(currentFilters)
  );
  const [isAlreadyLoading, setIsAlreadyLoading] = useState(false);
  const [loaded, setLoaded] = useState(items.length > 0);
  const [lastFetched, setLastFetched] = useState(
    JSON.stringify(currentFilters) + ":" + id + ":" + currentPage
  );

  const dispatch = useDispatch();

  const totalPages = useMemo(() => Math.ceil(pageMeta.total / PAGE_SIZE));
  useEffect(async () => {
    if (isAlreadyLoading) return;

    dispatch(setLastSeen(id));
    const cacheKey =
      JSON.stringify(currentFilters) + ":" + id + ":" + currentPage;

    if (loaded && lastFetched === cacheKey && !needsRefetching) return;
    setLastFetched(cacheKey);

    setIsAlreadyLoading(true);
    const delayedLoading = setTimeout(() => dispatch(setLoading(true)), 200);
    try {
      const data = await fetchingMethod({
        ...generateFilters(onlyVisible(currentFilters, visibleFilters)),
        page: currentPage,
      });
      if (id === "favorites") {
        dispatch(setFavoritesChanged(false));
      }
      dispatch(
        storingMethod({
          id,
          filters: data.filters,
          items: data.items,
          meta: {
            page: data.page,
            total: data.total,
          },
        })
      );
    } catch (err) {
      // TODO: Better show error state (given from the outside)
      dispatch(setGlobalError("errors.could_not_load_items"));
      sendError(err);
    }
    clearTimeout(delayedLoading);
    setIsAlreadyLoading(false);
    dispatch(setLoading(false));
    setLoaded(true);
  }, [id, currentPage, currentFilters, visibleFilters, isAlreadyLoading]);

  useEffect(async () => {
    const currentFilterCache = JSON.stringify(currentFilters);
    if (loaded && currentFilterCache !== lastCurrentFilters) {
      setLastCurrentFilters(currentFilterCache);
      dispatch(setCurrentPage({ id, page: 0 }));
    }
  }, [loaded, id, currentFilters, currentPage, lastCurrentFilters]);

  function onlyVisible(currentFilters, visibleFilters) {
    return currentFilters.filter((filter) => {
      return (
        visibleFilters[filter.type] &&
        visibleFilters[filter.type].findIndex((f) => f.value === filter.value) >
          -1
      );
    });
  }

  function prevPage() {
    dispatch(setCurrentPage({ id, page: Math.max(1, currentPage - 1) }));
    window.scrollTo(0, 0);
  }
  function nextPage() {
    dispatch(
      setCurrentPage({ id, page: Math.min(totalPages, currentPage + 1) })
    );
    window.scrollTo(0, 0);
  }

  function gotoPage(pageNum) {
    dispatch(setCurrentPage({ id, page: pageNum }));
    window.scrollTo(0, 0);
  }

  if (loaded && items.length === 0) {
    return <section>{renderEmptyState()}</section>;
  }
  return (
    <section>
      <div style={isLoading ? "opacity: 0.5;" : ""}>{items.map(render)}</div>
      <Pagination
        currentPage={currentPage}
        pageSize={PAGE_SIZE}
        totalPages={totalPages}
        prevPage={prevPage}
        nextPage={nextPage}
        gotoPage={gotoPage}
      />
    </section>
  );
}

const POSSIBLE_FILTERS = [
  "areaOfWork",
  "company",
  "language",
  "regions",
  "workRole",
  "skills",
  "state",
  "yearsOfExperience",
];
const generateFilters = (filters) =>
  POSSIBLE_FILTERS.reduce(
    (memo, type) => ({
      ...memo,
      [type]: filterSelected(filters.filter((f) => f.type === type)),
    }),
    {}
  );

function filterSelected(items) {
  if (!items) return undefined;
  return items
    .filter((x) => x.selected)
    .map((x) => x.value)
    .join(",");
}
